import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { pipe, path } from 'ramda';

import { useAsyncState } from 'utils/hooks';
import { wrapActions, wrapSelectorWithArg } from 'store/utils';

import { META, TASK_ID } from './consts';
import * as actions from './actions';
import { getSuccess, getError, getLoading } from './selectors';

export const useAlertsActions = wrapActions({ ...actions });

export const useSuccess = wrapSelectorWithArg(getSuccess);
export const useError = wrapSelectorWithArg(getError);
export const useLoading = wrapSelectorWithArg(getLoading);

export const useAlerts = (actionCreator) => {
  const index = useRef();
  const [id, setId] = useAsyncState();
  const { t } = useTranslation('common');

  const success = useSuccess(id);
  const error = useError(id);
  const loading = useLoading(id);
  const { setCancel } = useAlertsActions();

  const extractId = useCallback(
    (action) =>
      pipe(path([META, TASK_ID]), (value) => {
        index.current = value;
        setId(value);
      })(action),
    [setId]
  );

  const resetAlerts = useCallback(() => {
    if (!index.current) return;
    setId();
    setCancel(index.current);
    index.current = null;
  }, [setCancel, setId]);

  const handleAction = useCallback(
    (...args) => (typeof actionCreator === 'function' ? extractId(actionCreator(...args)) : null),
    [actionCreator, extractId]
  );

  useEffect(
    () => () => {
      if (index.current) setCancel(index.current);
    },
    [setCancel]
  );

  return {
    id,
    setId,
    action: handleAction,
    extractId,
    success: id && success,
    error: id && error && t(error),
    loading: id && loading,
    resetAlerts,
  };
};
